/**
 * Native Audio Player Hook
 *
 * Wraps HTML5 audio elements with Redux-based centralized control.
 * Ensures only one audio player is active at a time across the application.
 *
 * **Features:**
 * - Automatic coordination with other player types (WaveSurfer, Howler)
 * - Redux state management for unified audio control
 * - Play/Pause/Stop/Seek methods synced with global audio state
 * - Automatic pause when other audio starts playing
 *
 * **Usage:**
 * ```tsx
 * const audioRef = useRef<HTMLAudioElement>(null)
 * const { play, pause, stop, isPlaying, currentTime, duration } = useNativeAudioPlayer({
 *   audioRef,
 *   audioId: 'my-audio-123',
 *   audioUrl: 'https://example.com/audio.mp3'
 * })
 *
 * return (
 *   <>
 *     <audio ref={audioRef} src={audioUrl} />
 *     <button onClick={play}>Play</button>
 *     <button onClick={pause}>Pause</button>
 *   </>
 * )
 * ```
 */

import { useEffect, useCallback, useState, type RefObject } from 'react'
import { useAppDispatch, useAppSelector } from './useRedux'
import {
  playAudio,
  pauseAudio,
  resumeAudio,
  stopAudio,
  setCurrentTime,
  setDuration,
  selectCurrentAudioId,
  selectIsPlaying,
} from '@/store/slices/globalAudioSlice'

interface UseNativeAudioPlayerOptions {
  /** Ref to the HTML audio element */
  audioRef: RefObject<HTMLAudioElement | null>
  /** Unique identifier for this audio */
  audioId: string
  /** Audio URL */
  audioUrl: string
  /** Callback when playback starts */
  onPlay?: () => void
  /** Callback when playback pauses */
  onPause?: () => void
  /** Callback when playback stops */
  onStop?: () => void
}

export function useNativeAudioPlayer({
  audioRef,
  audioId,
  audioUrl,
  onPlay,
  onPause,
  onStop,
}: UseNativeAudioPlayerOptions) {
  const dispatch = useAppDispatch()

  // Redux state
  const currentAudioId = useAppSelector(selectCurrentAudioId)
  const globalIsPlaying = useAppSelector(selectIsPlaying)

  // Check if this player is the active one
  const isThisPlayerActive = currentAudioId === audioId
  const isPlaying = isThisPlayerActive && globalIsPlaying

  // Local state
  const [localCurrentTime, setLocalCurrentTime] = useState(0)
  const [localDuration, setLocalDuration] = useState(0)

  // Sync audio element playback with Redux state
  useEffect(() => {
    if (!audioRef.current) return

    const audio = audioRef.current

    if (isPlaying && isThisPlayerActive) {
      audio.play().catch(console.error)
    } else {
      audio.pause()
    }
  }, [isPlaying, isThisPlayerActive, audioRef])

  // Setup audio element event listeners
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = () => {
      const duration = audio.duration
      setLocalDuration(duration)
      dispatch(setDuration(duration))
    }

    const handleTimeUpdate = () => {
      const time = audio.currentTime
      setLocalCurrentTime(time)
      if (isThisPlayerActive) {
        dispatch(setCurrentTime(time))
      }
    }

    const handleEnded = () => {
      dispatch(stopAudio())
      onStop?.()
    }

    const handlePause = () => {
      if (!isPlaying) {
        onPause?.()
      }
    }

    const handlePlay = () => {
      if (isPlaying) {
        onPlay?.()
      }
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('play', handlePlay)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('play', handlePlay)
    }
  }, [audioRef, dispatch, isThisPlayerActive, isPlaying, onPlay, onPause, onStop])

  // Control methods
  const play = useCallback(() => {
    if (isThisPlayerActive) {
      // Resume this player
      dispatch(resumeAudio())
    } else {
      // Start playing this audio (stops any other playing audio via middleware)
      dispatch(playAudio({ audioUrl, audioId, playerType: 'native' }))
    }
    onPlay?.()
  }, [dispatch, audioUrl, audioId, isThisPlayerActive, onPlay])

  const pause = useCallback(() => {
    if (!isThisPlayerActive) return

    dispatch(pauseAudio())
    onPause?.()
  }, [dispatch, isThisPlayerActive, onPause])

  const stop = useCallback(() => {
    if (!isThisPlayerActive) return

    dispatch(stopAudio())
    if (audioRef.current) {
      // eslint-disable-next-line no-param-reassign
      audioRef.current.currentTime = 0
    }
    onStop?.()
  }, [dispatch, audioRef, isThisPlayerActive, onStop])

  const seek = useCallback(
    (time: number) => {
      if (!audioRef.current || !isThisPlayerActive) return

      // eslint-disable-next-line no-param-reassign
      audioRef.current.currentTime = time
      dispatch(setCurrentTime(time))
    },
    [audioRef, dispatch, isThisPlayerActive]
  )

  return {
    play,
    pause,
    stop,
    seek,
    isPlaying,
    currentTime: localCurrentTime,
    duration: localDuration,
    isThisPlayerActive,
  }
}
